import { useEffect, useState } from 'react';
import {
  useMutation, useQuery, useQueryClient, keepPreviousData,
  type UseMutationResult, type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import type {
  Category, IssueAction, IssueDetail, IssueEditInput, IssueFilters, IssueListPage,
  IssueSummary, NamedRef, TelegramStatus, Zone, ZoneMe, ZoneRoleName, ZoneStats,
} from './types.ts';

const ZONE_KEY = ['zone'] as const;

// ---- queries -------------------------------------------------------------

export function useZoneMe(): UseQueryResult<ZoneMe> {
  return useQuery({
    queryKey: ['zone', 'me'],
    queryFn: () => api.get<ZoneMe>('/zone/me').then((r) => r.data),
  });
}

/** `all` includes deactivated zones (org admins only; ignored by the server otherwise). */
export function useZones(all = false): UseQueryResult<Zone[]> {
  return useQuery({
    queryKey: ['zone', 'zones', { all }],
    queryFn: () => api.get<Zone[]>('/zone/zones', { params: all ? { all: 1 } : undefined }).then((r) => r.data),
  });
}

export function useCategories(all = false): UseQueryResult<Category[]> {
  return useQuery({
    queryKey: ['zone', 'categories', { all }],
    queryFn: () => api.get<Category[]>('/zone/categories', { params: all ? { all: 1 } : undefined }).then((r) => r.data),
  });
}

export function useZoneUsers(zoneRole?: ZoneRoleName): UseQueryResult<NamedRef[]> {
  return useQuery({
    queryKey: ['zone', 'users', { zoneRole: zoneRole ?? null }],
    queryFn: () => api.get<NamedRef[]>('/zone/users', { params: { zoneRole } }).then((r) => r.data),
  });
}

export function useIssues(filters: IssueFilters): UseQueryResult<IssueListPage> {
  const { mine, ...rest } = filters;
  return useQuery({
    queryKey: ['zone', 'issues', filters],
    queryFn: () =>
      api.get<IssueListPage>('/zone/issues', { params: { ...rest, mine: mine ? '1' : undefined } }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
}

export function useIssue(id: string): UseQueryResult<IssueDetail> {
  return useQuery({
    queryKey: ['zone', 'issue', id],
    queryFn: () => api.get<IssueDetail>(`/zone/issues/${id}`).then((r) => r.data),
  });
}

export function useZoneStats(): UseQueryResult<ZoneStats> {
  return useQuery({
    queryKey: ['zone', 'stats'],
    queryFn: () => api.get<ZoneStats>('/zone/stats').then((r) => r.data),
  });
}

/**
 * Photos need the Authorization header, so they are fetched as blobs and shown via an
 * object URL (revoked on unmount / change) — no tokens in URLs. Returns null while loading
 * or on failure.
 */
export function usePhotoUrl(issueId: string, photoId: string, thumb = false): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    api
      .get<Blob>(`/zone/issues/${issueId}/photos/${photoId}`, { params: thumb ? { thumb: 1 } : undefined, responseType: 'blob' })
      .then((r) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => { /* leave null — the UI shows a placeholder */ });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [issueId, photoId, thumb]);
  return url;
}

// ---- issue mutations -----------------------------------------------------

function useZoneInvalidate(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ZONE_KEY });
}

/** Body is multipart: text fields + repeated `photos` files (max 5). */
export function useCreateIssue(): UseMutationResult<IssueSummary & { photos: Array<{ id: string }> }, Error, FormData> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: (form: FormData) =>
      api.post<IssueSummary & { photos: Array<{ id: string }> }>('/zone/issues', form).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useEditIssue(): UseMutationResult<IssueSummary, Error, { id: string; patch: IssueEditInput }> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: ({ id, patch }) => api.patch<IssueSummary>(`/zone/issues/${id}`, patch).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useDeleteIssue(): UseMutationResult<void, Error, { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => api.delete(`/zone/issues/${id}`).then(() => undefined),
    // Skip the deleted issue itself: refetching it while its page is still mounted would flash a 404.
    onSuccess: (_d, { id }) => qc.invalidateQueries({
      queryKey: ZONE_KEY,
      predicate: (q) => !(q.queryKey[1] === 'issue' && q.queryKey[2] === id),
    }),
  });
}

export function useAssignIssue(): UseMutationResult<IssueSummary, Error, { id: string; categoryId: string; assigneeId: string }> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.post<IssueSummary>(`/zone/issues/${id}/assign`, body).then((r) => r.data),
    onSuccess: invalidate,
  });
}

/** start / confirm take no body; resolve takes an optional `note`; reopen requires `comment`. */
export function useIssueAction(
  action: IssueAction,
): UseMutationResult<IssueSummary, Error, { id: string; note?: string; comment?: string }> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: ({ id, note, comment }) => {
      const body = action === 'resolve' ? { note } : action === 'reopen' ? { comment } : {};
      return api.post<IssueSummary>(`/zone/issues/${id}/${action}`, body).then((r) => r.data);
    },
    onSuccess: invalidate,
  });
}

export function useAddComment(): UseMutationResult<unknown, Error, { id: string; comment: string }> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: ({ id, comment }) => api.post(`/zone/issues/${id}/comments`, { comment }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useAddPhotos(): UseMutationResult<{ photos: Array<{ id: string }> }, Error, { id: string; form: FormData }> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: ({ id, form }) =>
      api.post<{ photos: Array<{ id: string }> }>(`/zone/issues/${id}/photos`, form).then((r) => r.data),
    onSuccess: invalidate,
  });
}

// ---- zone admin (org admin only) -----------------------------------------

export interface ZoneAdmin {
  createZone: UseMutationResult<Zone, Error, { name: string }>;
  updateZone: UseMutationResult<unknown, Error, { id: string; name?: string; isActive?: boolean }>;
  setCouncillors: UseMutationResult<unknown, Error, { id: string; userIds: string[] }>;
  createCategory: UseMutationResult<Category, Error, { name: string }>;
  updateCategory: UseMutationResult<Category, Error, { id: string; name?: string; isActive?: boolean }>;
}

export function useZoneAdmin(): ZoneAdmin {
  const invalidate = useZoneInvalidate();
  const onSuccess = invalidate;
  const createZone = useMutation({
    mutationFn: (b: { name: string }) => api.post<Zone>('/zone/zones', b).then((r) => r.data),
    onSuccess,
  });
  const updateZone = useMutation({
    mutationFn: ({ id, ...b }: { id: string; name?: string; isActive?: boolean }) =>
      api.patch(`/zone/zones/${id}`, b).then((r) => r.data),
    onSuccess,
  });
  const setCouncillors = useMutation({
    mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>
      api.put(`/zone/zones/${id}/councillors`, { userIds }).then((r) => r.data),
    onSuccess,
  });
  const createCategory = useMutation({
    mutationFn: (b: { name: string }) => api.post<Category>('/zone/categories', b).then((r) => r.data),
    onSuccess,
  });
  const updateCategory = useMutation({
    mutationFn: ({ id, ...b }: { id: string; name?: string; isActive?: boolean }) =>
      api.patch<Category>(`/zone/categories/${id}`, b).then((r) => r.data),
    onSuccess,
  });
  return { createZone, updateZone, setCouncillors, createCategory, updateCategory };
}

// ---- telegram (Profile) --------------------------------------------------

export function useTelegramStatus(): UseQueryResult<TelegramStatus> {
  return useQuery({
    queryKey: ['zone', 'telegram'],
    queryFn: () => api.get<TelegramStatus>('/telegram/status').then((r) => r.data),
  });
}

/** Resolves with the t.me deep link to open. */
export function useTelegramLink(): UseMutationResult<{ url: string }, Error, void> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: () => api.post<{ url: string }>('/telegram/link').then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useTelegramUnlink(): UseMutationResult<unknown, Error, void> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: () => api.post('/telegram/unlink').then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useTelegramPrefs(): UseMutationResult<{ telegramNotifications: boolean }, Error, boolean> {
  const invalidate = useZoneInvalidate();
  return useMutation({
    mutationFn: (telegramNotifications: boolean) =>
      api.patch<{ telegramNotifications: boolean }>('/telegram/prefs', { telegramNotifications }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

